import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { browseTags, listBooks, listCollections } from "../api";
import { BookList, NothingPublished } from "../components/BookList";
import CollectionRow from "../components/CollectionRow";
import { BrowseTagChips } from "../components/Tags";
import { useT } from "../i18n";

/**
 * Browse is the catalogue: every book published here, newest first.
 *
 * This is what `/` used to be (ADR-0060). It answers "what is there to read?",
 * which is a different question from "what am I reading?" and wants the
 * opposite order and the opposite size — so it is a different screen, not a
 * filter on one. The query and the endpoint are unchanged, and it needs no
 * account: a published book is readable by anyone.
 *
 * A tag chip narrows the same listing to the books carrying it (0119 T3); the
 * tag is in the address, so the book page's tags link here and Back undoes a
 * tap. The collection row stands aside while a tag is chosen.
 *
 * A chip reloads only the grid (the owner, 10-19): the old books stay, faded,
 * until the tag's arrive, so the page neither flashes its bones nor shrinks
 * under the reader's scroll; the row closes rather than vanishes for the same
 * reason. Under the collections the grid is headed "All books", and under a
 * tag it is named for the tag; with neither, the page's title names it.
 */
export default function Browse() {
  const t = useT();
  const [params, setParams] = useSearchParams();
  const tag = params.get("tag");
  const { data, isPending, isError, error, refetch, isPlaceholderData } = useQuery({
    queryKey: tag ? ["books", "public", { tag }] : ["books", "public"],
    queryFn: () => listBooks(undefined, tag ?? undefined),
    placeholderData: keepPreviousData,
  });
  // The chips' and the row's own reads, shared through the cache.
  const tags = useQuery({ queryKey: ["tags", "browse"], queryFn: browseTags }).data?.items;
  const collections = useQuery({ queryKey: ["collections"], queryFn: listCollections }).data?.items;
  const heading = tag
    ? tags?.find((x) => x.id === tag)?.name
    : collections && collections.length > 0
      ? t.browse.allBooks
      : undefined;

  return (
    <main className="mx-auto max-w-5xl px-4 pt-5 pb-12">
      <h1 className="mb-4 text-lg font-semibold tracking-tight">{t.browse.title}</h1>

      <BrowseTagChips
        selected={tag}
        onSelect={(id) => setParams(id ? { tag: id } : {}, { replace: false })}
      />
      <div
        data-testid="collection-row-frame"
        inert={tag ? true : undefined}
        className={`grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none ${
          tag ? "invisible grid-rows-[0fr]" : "grid-rows-[1fr]"
        }`}
      >
        <div className="min-h-0 overflow-hidden">
          <CollectionRow />
        </div>
      </div>

      {heading && (
        <h2
          data-testid="grid-heading"
          dir={tag ? "auto" : undefined}
          className="mb-2 text-sm font-semibold text-muted"
        >
          {heading}
        </h2>
      )}
      <div
        data-testid="book-grid"
        aria-busy={isPlaceholderData || undefined}
        className={`transition-opacity duration-150 ${
          isPlaceholderData ? "pointer-events-none opacity-35" : ""
        }`}
      >
        <BookList
          books={data?.items ?? []}
          isPending={isPending}
          error={isError ? (error as Error) : null}
          onRetry={() => void refetch()}
          empty={
            tag ? (
              <p data-testid="tag-nothing" className="text-sm text-muted">
                {t.tag.nothing}
              </p>
            ) : (
              <NothingPublished />
            )
          }
          savable
          seriesCount
        />
      </div>
    </main>
  );
}
